/**
 * Inventory Cache Utility
 * Manages caching for item counts and items data with validation and sync detection
 */

const CACHE_KEYS = {
  COUNTS: 'tidy_inventory_counts',
  ITEMS: 'tidy_inventory_items',
  METADATA: 'tidy_cache_metadata',
} as const;

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

interface CountsCache {
  all: number;
  wandering: number;
  shared_with_me: number;
  by_collection: Record<string, number>;
  timestamp: number;
}

interface ItemsCache {
  [collectionId: string]: {
    data: any[];
    timestamp: number;
  };
}

interface CacheMetadata {
  lastSync: number;
  version: string;
}

/**
 * Get cached counts
 */
export const getCachedCounts = (spaceId?: number): CountsCache | null => {
  try {
    const key = spaceId ? `${CACHE_KEYS.COUNTS}_s${spaceId}` : CACHE_KEYS.COUNTS;
    const cached = localStorage.getItem(key);
    if (!cached) return null;

    const data = JSON.parse(cached) as CountsCache;
    const age = Date.now() - data.timestamp;

    // Return cached data if less than TTL
    if (age < CACHE_TTL) {
      return data;
    }

    return null;
  } catch (error) {
    console.error('[Cache] Failed to get cached counts:', error);
    return null;
  }
};

/**
 * Set cached counts
 */
export const setCachedCounts = (counts: Omit<CountsCache, 'timestamp'>, spaceId?: number): void => {
  try {
    const data: CountsCache = {
      ...counts,
      timestamp: Date.now(),
    };
    const key = spaceId ? `${CACHE_KEYS.COUNTS}_s${spaceId}` : CACHE_KEYS.COUNTS;
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error('[Cache] Failed to set cached counts:', error);
  }
};

/**
 * Get cached items for a specific collection
 */
export const getCachedItems = (collectionId: string, spaceId?: number): any[] | null => {
  try {
    const cached = localStorage.getItem(CACHE_KEYS.ITEMS);
    if (!cached) return null;

    const allItems = JSON.parse(cached) as ItemsCache;
    const key = (collectionId === 'all' || collectionId === 'wandering') && spaceId 
      ? `${collectionId}_s${spaceId}` 
      : collectionId;
    
    const collectionData = allItems[key];

    if (!collectionData) return null;

    const age = Date.now() - collectionData.timestamp;

    // Return cached data if less than TTL
    if (age < CACHE_TTL) {
      return collectionData.data;
    }

    return null;
  } catch (error) {
    console.error('[Cache] Failed to get cached items:', error);
    return null;
  }
};

/**
 * Set cached items for a specific collection
 */
export const setCachedItems = (collectionId: string, items: any[], spaceId?: number): void => {
  try {
    const cached = localStorage.getItem(CACHE_KEYS.ITEMS);
    const allItems: ItemsCache = cached ? JSON.parse(cached) : {};

    const key = (collectionId === 'all' || collectionId === 'wandering') && spaceId 
      ? `${collectionId}_s${spaceId}` 
      : collectionId;

    allItems[key] = {
      data: items,
      timestamp: Date.now(),
    };

    localStorage.setItem(CACHE_KEYS.ITEMS, JSON.stringify(allItems));
  } catch (error) {
    console.error('[Cache] Failed to set cached items:', error);
  }
};

/**
 * Validate items against expected count
 * Returns true if items match the expected count, false if out of sync
 */
export const validateItemsCount = (
  collectionId: string,
  items: any[],
  expectedCount: number
): boolean => {
  const actualCount = items.length;
  
  if (actualCount !== expectedCount) {
    console.warn(
      `[Cache] Count mismatch for collection ${collectionId}: ` +
      `expected ${expectedCount}, got ${actualCount}. Data may be out of sync.`
    );
    return false;
  }
  
  return true;
};

/**
 * Invalidate cache for a specific collection
 */
export const invalidateCollectionCache = (collectionId: string): void => {
  try {
    const cached = localStorage.getItem(CACHE_KEYS.ITEMS);
    if (!cached) return;

    const allItems = JSON.parse(cached) as ItemsCache;
    delete allItems[collectionId];

    localStorage.setItem(CACHE_KEYS.ITEMS, JSON.stringify(allItems));
  } catch (error) {
    console.error('[Cache] Failed to invalidate collection cache:', error);
  }
};

/**
 * Invalidate all counts cache
 */
export const invalidateCountsCache = (): void => {
  try {
    localStorage.removeItem(CACHE_KEYS.COUNTS);
  } catch (error) {
    console.error('[Cache] Failed to invalidate counts cache:', error);
  }
};

/**
 * Invalidate all cache (counts and items)
 */
export const invalidateAllCache = (): void => {
  try {
    localStorage.removeItem(CACHE_KEYS.COUNTS);
    localStorage.removeItem(CACHE_KEYS.ITEMS);
    localStorage.removeItem(CACHE_KEYS.METADATA);
  } catch (error) {
    console.error('[Cache] Failed to invalidate all cache:', error);
  }
};

/**
 * Get recently accessed collections for prefetching
 */
export const getRecentCollections = (limit: number = 3): string[] => {
  try {
    const recentKey = 'tidy_recent_collections';
    const stored = localStorage.getItem(recentKey);
    if (!stored) return [];

    const times = JSON.parse(stored) as Record<string, number>;
    return Object.entries(times)
      .sort(([, a], [, b]) => b - a)
      .slice(0, limit)
      .map(([id]) => id);
  } catch (error) {
    console.error('[Cache] Failed to get recent collections:', error);
    return [];
  }
};

/**
 * Prefetch items for recently accessed collections
 */
export const prefetchRecentCollections = async (
  token: string,
  currentCollection: string,
  apiBaseUrl: string
): Promise<void> => {
  const recentCollections = getRecentCollections(3);
  
  for (const collectionId of recentCollections) {
    // Skip current collection (already fetched) and pseudo-collections
    if (
      collectionId === currentCollection ||
      collectionId === 'all' ||
      collectionId === 'wandering' ||
      collectionId === 'shared_with_me'
    ) {
      continue;
    }

    // Check if already cached
    const cached = getCachedItems(collectionId);
    if (cached) continue;

    // Prefetch with delay to not interfere with user interaction
    setTimeout(async () => {
      try {
        const response = await fetch(
          `${apiBaseUrl}/api/items?collection=${collectionId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.ok) {
          const data = await response.json();
          const items = Array.isArray(data) ? data : data.items || [];
          setCachedItems(collectionId, items);
          console.log(`[Cache] Prefetched ${items.length} items for collection ${collectionId}`);
        }
      } catch (error) {
        console.error(`[Cache] Failed to prefetch collection ${collectionId}:`, error);
      }
    }, 1000); // 1 second delay
  }
};

/**
 * Update cache metadata
 */
export const updateCacheMetadata = (): void => {
  try {
    const metadata: CacheMetadata = {
      lastSync: Date.now(),
      version: '1.0',
    };
    localStorage.setItem(CACHE_KEYS.METADATA, JSON.stringify(metadata));
  } catch (error) {
    console.error('[Cache] Failed to update cache metadata:', error);
  }
};
